import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Paper,
  Typography,
  IconButton,
  Box,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  daySection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  dayHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  dayTitle: {
    flex: 1,
    fontWeight: "bold",
  },
  periodRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1),
  },
  timeField: {
    minWidth: 120,
  },
  addPeriodButton: {
    marginTop: theme.spacing(1),
  },
  separator: {
    margin: `0 ${theme.spacing(1)}px`,
  },
}));

const DAYS_OF_WEEK = [
  { key: "monday", label: "Segunda-feira" },
  { key: "tuesday", label: "Terça-feira" },
  { key: "wednesday", label: "Quarta-feira" },
  { key: "thursday", label: "Quinta-feira" },
  { key: "friday", label: "Sexta-feira" },
  { key: "saturday", label: "Sábado" },
  { key: "sunday", label: "Domingo" },
];

const WorkHoursSchedule = ({ value, onChange, disabled = false }) => {
  const classes = useStyles();
  const [workHours, setWorkHours] = useState(
    value || {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    }
  );

  useEffect(() => {
    if (value) {
      setWorkHours(value);
    }
  }, [value]);

  const handleDayToggle = (dayKey) => {
    const newWorkHours = { ...workHours };
    if (newWorkHours[dayKey] && newWorkHours[dayKey].length > 0) {
      // Se já tem períodos, remove todos
      newWorkHours[dayKey] = [];
    } else {
      // Se não tem períodos, adiciona um período padrão
      newWorkHours[dayKey] = [{ start: "08:00", end: "18:00" }];
    }
    setWorkHours(newWorkHours);
    onChange && onChange(newWorkHours);
  };

  const handleAddPeriod = (dayKey) => {
    const newWorkHours = { ...workHours };
    if (!newWorkHours[dayKey]) {
      newWorkHours[dayKey] = [];
    }
    newWorkHours[dayKey].push({ start: "08:00", end: "18:00" });
    setWorkHours(newWorkHours);
    onChange && onChange(newWorkHours);
  };

  const handleRemovePeriod = (dayKey, periodIndex) => {
    const newWorkHours = { ...workHours };
    if (newWorkHours[dayKey]) {
      newWorkHours[dayKey].splice(periodIndex, 1);
      setWorkHours(newWorkHours);
      onChange && onChange(newWorkHours);
    }
  };

  const handleTimeChange = (dayKey, periodIndex, field, time) => {
    const newWorkHours = { ...workHours };
    if (!newWorkHours[dayKey]) {
      newWorkHours[dayKey] = [];
    }
    if (!newWorkHours[dayKey][periodIndex]) {
      newWorkHours[dayKey][periodIndex] = { start: "08:00", end: "18:00" };
    }
    newWorkHours[dayKey][periodIndex][field] = time;
    setWorkHours(newWorkHours);
    onChange && onChange(newWorkHours);
  };

  const isDayEnabled = (dayKey) => {
    return workHours[dayKey] && workHours[dayKey].length > 0;
  };

  return (
    <div className={classes.root}>
      <Typography variant="subtitle2" gutterBottom>
        {i18n.t("userModal.form.workHours") || "Horários de Atendimento"}
      </Typography>
      <Typography variant="caption" color="textSecondary" gutterBottom>
        {i18n.t("userModal.form.workHoursHelper") ||
          "Configure os horários de atendimento para cada dia da semana. Você pode adicionar múltiplos períodos por dia."}
      </Typography>

      {DAYS_OF_WEEK.map((day) => {
        const dayKey = day.key;
        const periods = workHours[dayKey] || [];
        const enabled = isDayEnabled(dayKey);

        return (
          <Paper key={dayKey} className={classes.daySection} elevation={0}>
            <div className={classes.dayHeader}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enabled}
                    onChange={() => handleDayToggle(dayKey)}
                    disabled={disabled}
                    color="primary"
                  />
                }
                label={
                  <Typography className={classes.dayTitle}>
                    {day.label}
                  </Typography>
                }
              />
            </div>

            {enabled && (
              <Box>
                {periods.map((period, periodIndex) => (
                  <div key={periodIndex} className={classes.periodRow}>
                    <TextField
                      className={classes.timeField}
                      type="time"
                      label="Início"
                      value={period.start || "08:00"}
                      onChange={(e) =>
                        handleTimeChange(dayKey, periodIndex, "start", e.target.value)
                      }
                      disabled={disabled}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 300, // 5 minutos
                      }}
                      variant="outlined"
                      size="small"
                      margin="dense"
                    />
                    <Typography className={classes.separator}>às</Typography>
                    <TextField
                      className={classes.timeField}
                      type="time"
                      label="Fim"
                      value={period.end || "18:00"}
                      onChange={(e) =>
                        handleTimeChange(dayKey, periodIndex, "end", e.target.value)
                      }
                      disabled={disabled}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        step: 300, // 5 minutos
                      }}
                      variant="outlined"
                      size="small"
                      margin="dense"
                    />
                    {periods.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemovePeriod(dayKey, periodIndex)}
                        disabled={disabled}
                        color="secondary"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </div>
                ))}
                <Button
                  className={classes.addPeriodButton}
                  startIcon={<AddIcon />}
                  onClick={() => handleAddPeriod(dayKey)}
                  disabled={disabled}
                  size="small"
                  variant="outlined"
                  color="primary"
                >
                  {i18n.t("userModal.form.addPeriod") || "Adicionar Período"}
                </Button>
              </Box>
            )}
          </Paper>
        );
      })}
    </div>
  );
};

export default WorkHoursSchedule;
